<template>
    <div v-if="isMobile" class="shifts-days">
        <v-slide-group v-model="day" mandatory show-arrows="desktop">
            <v-slide-item v-for="item in days" :key="item.value" :value="item.value" v-slot="{ active, toggle }">
                <button type="button" class="day-tab" :class="{ 'day-tab--active': active }" @click="toggle">
                    {{ item.name }}
                </button>
            </v-slide-item>
        </v-slide-group>

        <div class="day-list">
            <div v-for="shift in daySorted" :key="shift.id" class="day-row" :style="cardStyle(shift)"
                @click="$emit('click:shift', shift)">
                <span class="day-dot" :style="{ background: activityColor(shift) }" />
                <div class="day-info">
                    <span class="day-name" :style="{ color: activityColor(shift) }">{{ activityName(shift) }}</span>
                    <span class="day-time">{{ shift.startTime }} - {{ shift.endTime }}</span>
                </div>
                <span class="shift-seats" :class="seatsClass(shift)">
                    {{ $t('business.seats_taken', { taken: taken(shift), total: shift.capacity }) }}
                </span>
            </div>

            <span v-if="daySorted.length === 0" class="day-empty">{{ $t('business.no_shifts_day') }}</span>

            <button type="button" class="day-add" @click="$emit('create', { day, startTime: '08:00' })">
                <v-icon size="15">mdi-plus</v-icon>
                {{ $t('business.new_shift') }}
            </button>
        </div>
    </div>

    <div v-else class="shifts-grid-wrap">
        <div class="shifts-grid" :style="gridStyle">
            <div class="grid-corner" />
            <div v-for="item in days" :key="`head-${item.value}`" class="grid-head">
                <span>{{ item.name }}</span>
                <span class="grid-head-date">{{ item.date }}</span>
            </div>

            <div class="hour-col" :style="{ height: `${bodyHeight}px` }">
                <span v-for="hour in hours" :key="`hour-${hour}`" class="hour-label" :style="hourStyle(hour)">
                    {{ hourLabel(hour) }}
                </span>
            </div>

            <div v-for="item in days" :key="`col-${item.value}`" class="day-col"
                :class="{ 'day-col--over': dragOverDay === item.value }" :style="{ height: `${bodyHeight}px` }"
                @mousemove="onHover($event, item.value)" @mouseleave="clearHover"
                @click="onColumnClick($event, item.value)" @dragover="onDragOver($event, item.value)"
                @dragleave="onDragLeave(item.value)" @drop="onDrop($event, item.value)">
                <span v-if="hoverDay === item.value && !dragging" class="hover-add" :style="{ top: `${hoverTop}px` }">
                    + {{ hoverTime }}
                </span>

                <div v-for="entry in layout(item.value)" :key="entry.shift.id" class="shift-card"
                    :class="{ 'shift-card--dragging': draggingId === entry.shift.id }"
                    :style="[cardStyle(entry.shift), entryStyle(entry)]" draggable="true"
                    @click.stop="$emit('click:shift', entry.shift)" @dragstart="onDragStart($event, entry.shift)"
                    @dragend="onDragEnd">
                    <button-tooltip top :tooltip="$t('business.edit_shift')">
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="shift-edit" v-on="on" v-bind="attrs"
                                :style="{ color: activityColor(entry.shift) }" @click.stop="$emit('edit', entry.shift)">
                                <v-icon size="11">mdi-pencil</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                    <span class="shift-name" :style="{ color: activityColor(entry.shift) }">
                        {{ activityName(entry.shift) }}
                    </span>
                    <span v-if="entry.height >= 30" class="shift-time">
                        {{ entry.shift.startTime }} - {{ entry.shift.endTime }}
                    </span>
                    <span v-if="entry.height >= 46" class="shift-seats" :class="seatsClass(entry.shift)">
                        {{ $t('business.seats_taken', { taken: taken(entry.shift), total: entry.shift.capacity }) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ButtonTooltip from "@/components/common/ButtonTooltip";
import {
    DAY_END_HOUR, DAY_START_HOUR, WEEK_DAYS, isShiftFull, memberCount, toMinutes, toTime,
} from "@/utils/Business";

const ROW_HEIGHT = 56

const SNAP_MINUTES = 30

const HOVER_HEIGHT = 26

const ALMOST_FULL = 0.8

export default {
    name: "ShiftsGrid",
    components: { ButtonTooltip },
    props: {
        shifts: {
            type: Array,
            default: () => [],
        },
        activities: {
            type: Array,
            default: () => [],
        },
        bookings: {
            type: Object,
            default: null,
        },
        weekStart: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            day: 1,
            hoverDay: null,
            hoverTime: '',
            hoverTop: 0,
            dragging: false,
            draggingId: null,
            dragOverDay: null,
        }
    },
    computed: {
        daySorted(vm = this) {
            return vm.shifts
                .filter(shift => shift.day === vm.day)
                .sort((a, b) => a.startTime.localeCompare(b.startTime))
        },
        days(vm = this) {
            void vm.$i18n.locale
            const monday = vm.weekStart ? vm.$moment(vm.weekStart, 'YYYY-MM-DD') : vm.$moment().isoWeekday(1)
            return WEEK_DAYS.map(value => ({
                value,
                name: vm.$moment().isoWeekday(value).format('ddd').replace('.', '').toUpperCase(),
                date: monday.clone().add(value - 1, 'days').format('DD/MM'),
            }))
        },
        range(vm = this) {
            let first = DAY_START_HOUR
            let last = DAY_END_HOUR
            vm.shifts.forEach(shift => {
                first = Math.min(first, Math.floor(toMinutes(shift.startTime) / 60))
                last = Math.max(last, Math.ceil(toMinutes(shift.endTime) / 60))
            })
            return { first, last: Math.min(24, last) }
        },
        hours(vm = this) {
            const hours = []
            for (let hour = vm.range.first; hour <= vm.range.last; hour += 1) hours.push(hour)
            return hours
        },
        bodyHeight(vm = this) {
            return (vm.hours.length - 1) * ROW_HEIGHT
        },
        gridStyle(vm = this) {
            return { gridTemplateColumns: `56px repeat(${vm.days.length}, minmax(130px, 1fr))` }
        },
    },
    created() {
        this.day = this.$moment().isoWeekday()
    },
    methods: {
        taken(shift) {
            return memberCount(shift, this.bookings)
        },
        hourLabel(hour) {
            return `${String(hour % 24).padStart(2, '0')}:00`
        },
        offsetFor(minutes) {
            return (minutes - this.range.first * 60) / 60 * ROW_HEIGHT
        },
        hourStyle(hour) {
            const vm = this
            const top = { top: `${vm.offsetFor(hour * 60)}px` }
            if (hour === vm.range.first) return { ...top, transform: 'translateY(2px)' }
            if (hour === vm.range.last) return { ...top, transform: 'translateY(-14px)' }
            return top
        },
        layout(day) {
            const vm = this
            const items = vm.shifts
                .filter(shift => shift.day === day)
                .map(shift => ({ shift, start: toMinutes(shift.startTime), end: toMinutes(shift.endTime) }))
                .sort((a, b) => a.start - b.start || a.end - b.end)

            const clusters = []
            let current = []
            let clusterEnd = -1

            items.forEach(item => {
                if (current.length === 0 || item.start < clusterEnd) {
                    current.push(item)
                    clusterEnd = Math.max(clusterEnd, item.end)
                    return
                }
                clusters.push(current)
                current = [item]
                clusterEnd = item.end
            })
            if (current.length > 0) clusters.push(current)

            const entries = []
            clusters.forEach(cluster => {
                const columnEnds = []
                cluster.forEach(item => {
                    let column = columnEnds.findIndex(end => end <= item.start)
                    if (column === -1) {
                        column = columnEnds.length
                        columnEnds.push(item.end)
                    } else {
                        columnEnds[column] = item.end
                    }
                    item.column = column
                })
                cluster.forEach(item => entries.push({
                    shift: item.shift,
                    top: vm.offsetFor(item.start),
                    height: Math.max(20, (item.end - item.start) / 60 * ROW_HEIGHT - 2),
                    column: item.column,
                    columns: columnEnds.length,
                }))
            })
            return entries
        },
        entryStyle(entry) {
            const width = 100 / entry.columns
            return {
                top: `${entry.top}px`,
                height: `${entry.height}px`,
                left: `calc(${entry.column * width}% + 2px)`,
                width: `calc(${width}% - 4px)`,
            }
        },
        timeFromEvent(event) {
            const vm = this
            const rect = event.currentTarget.getBoundingClientRect()
            const offset = Math.max(0, Math.min(rect.height, event.clientY - rect.top))
            const raw = vm.range.first * 60 + (offset / ROW_HEIGHT) * 60
            const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES
            const last = vm.range.last * 60 - SNAP_MINUTES
            return toTime(Math.max(vm.range.first * 60, Math.min(last, snapped)))
        },
        onHover(event, day) {
            const vm = this
            if (vm.dragging) return vm.clearHover()
            if (event.target.closest('.shift-card')) return vm.clearHover()
            const time = vm.timeFromEvent(event)
            vm.hoverDay = day
            vm.hoverTime = time
            vm.hoverTop = Math.min(vm.offsetFor(toMinutes(time)), vm.bodyHeight - HOVER_HEIGHT)
        },
        clearHover() {
            this.hoverDay = null
        },
        onColumnClick(event, day) {
            if (event.target.closest('.shift-card')) return
            this.$emit('create', { day, startTime: this.timeFromEvent(event) })
        },
        onDragStart(event, shift) {
            const vm = this
            vm.dragging = true
            vm.draggingId = shift.id
            vm.clearHover()
            event.dataTransfer.effectAllowed = 'copy'
            event.dataTransfer.setData('text/plain', shift.id)
        },
        onDragEnd() {
            const vm = this
            vm.dragging = false
            vm.draggingId = null
            vm.dragOverDay = null
        },
        onDragOver(event, day) {
            if (!this.dragging) return
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
            this.dragOverDay = day
        },
        onDragLeave(day) {
            if (this.dragOverDay === day) this.dragOverDay = null
        },
        onDrop(event, day) {
            const vm = this
            if (!vm.draggingId) return
            event.preventDefault()
            const shift = vm.shifts.find(item => item.id === vm.draggingId)
            const startTime = vm.timeFromEvent(event)
            vm.onDragEnd()
            if (!shift) return
            if (shift.day === day && shift.startTime === startTime) return
            vm.$emit('copy', { shift, day, startTime })
        },
        seatsClass(shift) {
            const taken = this.taken(shift)
            if (isShiftFull(shift, this.bookings)) return 'shift-seats--full'
            if (shift.capacity > 0 && taken >= shift.capacity * ALMOST_FULL) return 'shift-seats--almost'
            return ''
        },
        activity(shift) {
            return this.activities.find(item => item.id === shift.activityId) || null
        },
        activityName(shift) {
            return this.activity(shift)?.name || this.$t('business.no_activity')
        },
        activityColor(shift) {
            return this.activity(shift)?.color || '#8892A4'
        },
        cardStyle(shift) {
            const color = this.activityColor(shift)
            return {
                background: `${color}29`,
                borderColor: `${color}66`,
            }
        },
    },
}
</script>

<style scoped>
.shifts-grid-wrap {
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    background: var(--v-surface-base);
}

.shifts-grid {
    display: grid;
    min-width: 980px;
}

.grid-corner {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 3;
    background: var(--v-surface2-base);
    border-bottom: 1px solid var(--v-outline-base);
    border-right: 1px solid var(--v-outline-base);
}

.grid-head {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 9px 8px;
    background: var(--v-surface2-base);
    border-bottom: 1px solid var(--v-outline-base);
    border-right: 1px solid var(--v-outline-base);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: var(--v-txtSecondary-base);
}

.grid-head-date {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0;
    color: var(--v-txtMuted-base);
}

.hour-col {
    position: sticky;
    left: 0;
    z-index: 1;
    background: var(--v-surface-base);
    border-right: 1px solid var(--v-outline-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.hour-label {
    position: absolute;
    left: 0;
    right: 8px;
    transform: translateY(-6px);
    text-align: right;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.day-col {
    position: relative;
    cursor: pointer;
    border-right: 1px solid var(--v-outline-base);
    border-bottom: 1px solid var(--v-outline-base);
    background-image: linear-gradient(to bottom, var(--v-outline-base) 1px, transparent 1px);
    background-size: 100% 56px;
    background-repeat: repeat-y;
    transition: background-color 0.12s;
}

.day-col--over {
    background-color: var(--v-surface2-base);
}

.hover-add {
    position: absolute;
    left: 2px;
    right: 2px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    border: 1px dashed var(--v-outline2-base);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.shift-card {
    position: absolute;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 3px 7px;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: grab;
    transition: filter 0.15s, opacity 0.15s;
}

.shift-card:hover {
    z-index: 5;
    filter: brightness(1.15);
}

.shift-card--dragging {
    opacity: 0.35;
}

.shift-edit {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.22);
    opacity: 0;
    transition: opacity 0.15s;
}

.shift-card:hover .shift-edit {
    opacity: 0.85;
}

.shift-name {
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 14px;
}

.shift-time {
    margin-top: 1px;
    font-size: 10px;
    white-space: nowrap;
    color: var(--v-txtSecondary-base);
}

.shift-seats {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;
    color: var(--v-txtMuted-base);
}

.shift-seats--almost {
    color: #E6AC00;
}

.shift-seats--full {
    color: var(--v-error-base);
}

.shifts-days {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.day-tab {
    min-width: 46px;
    height: 32px;
    padding: 0 12px;
    margin-right: 6px;
    border-radius: 9px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    font-size: var(--fs-caption);
    font-weight: 700;
    text-transform: capitalize;
    color: var(--v-txtSecondary-base);
    transition: border-color 0.15s, color 0.15s;
}

.day-tab--active {
    border-color: var(--v-primary-base);
    color: var(--v-txtPrimary-base);
}

.day-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.day-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 11px 13px;
    border: 1px solid transparent;
    border-radius: 10px;
    cursor: pointer;
}

.day-dot {
    width: 8px;
    height: 8px;
    flex-shrink: 0;
    border-radius: 50%;
}

.day-info {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.day-name {
    font-size: var(--fs-label);
    font-weight: 700;
}

.day-time {
    font-size: var(--fs-caption);
    color: var(--v-txtSecondary-base);
}

.day-empty {
    padding: 24px 0;
    text-align: center;
    font-size: var(--fs-caption);
    color: var(--v-txtMuted-base);
}

.day-add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 40px;
    border: 1px dashed var(--v-outline2-base);
    border-radius: 10px;
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.day-add:hover {
    color: var(--v-txtPrimary-base);
    border-color: var(--v-txtMuted-base);
}

.theme--light .day-tab--active {
    border-color: var(--v-accent-base);
}
</style>
